import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { UserService } from '../../../layout/service/user.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';


@Component({
    selector: 'app-sheet-user-answer-detail',
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule],
    template: `
<div [ngClass]="inline ? 'bg-blue-50 rounded-xl p-4 border border-blue-200' : 'min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 py-10 px-2'">
    <div [ngClass]="inline ? 'bg-white rounded-xl shadow-sm border border-blue-100 p-4' : 'mx-auto bg-white rounded-3xl shadow-xl border border-blue-100 p-8'">
        <button *ngIf="!inline" (click)="close.emit()" class="mb-6 px-4 py-2 rounded-lg bg-blue-100 text-blue-700 font-semibold shadow hover:bg-blue-200 transition">
            ← Kembali
        </button>
        <h2 [ngClass]="inline ? 'text-lg md:text-xl font-bold text-blue-900 mb-4 text-center' : 'text-3xl md:text-4xl font-extrabold text-blue-900 mb-8 text-center drop-shadow-sm tracking-tight'">
            {{ inline ? 'Jawaban Peserta' : 'Detail Jawaban Siswa' }}
        </h2>
        <ng-container *ngIf="!loading; else loadingTpl">
            <ng-container *ngIf="data; else noAnswers">
            <div [ngClass]="inline ? 'grid grid-cols-1 gap-3 mb-4' : 'grid grid-cols-1 md:grid-cols-2 gap-6 mb-8'">
                <div>
                    <span class="block font-semibold text-blue-700 mb-1">Nama Siswa</span>
                    <span [ngClass]="inline ? 'block text-sm font-medium text-gray-900' : 'block text-lg font-medium text-gray-900'">{{ user?.name || data.user_fullname || '-' }}</span>
                </div>
                <div *ngIf="!inline">
                    <span class="block font-semibold text-blue-700 mb-1">Kasus</span>
                    <span class="block text-lg font-medium text-gray-900">{{ data.sheet_case_name }}</span>
                </div>
                <div *ngIf="!inline">
                    <span class="block font-semibold text-blue-700 mb-1">Tanggal</span>
                    <span class="block text-lg font-medium text-gray-900">{{ data.created_at | date:'dd MMM yyyy, HH:mm' }}</span>
                </div>
                <div *ngIf="lastRefreshTime && !inline" class="text-xs text-gray-500">
                    <span class="pi pi-clock mr-1"></span>
                    Diperbarui: {{ getLastRefreshTimeFormatted() }}
                </div>
            </div>
            
            <!-- Auto-refresh indicator for inline view -->
            <div *ngIf="inline && lastRefreshTime" class="text-xs text-gray-500 mb-2 flex items-center justify-center gap-1">
                <span class="pi pi-sync"></span>
                <span>{{ getLastRefreshTimeFormatted() }}</span>
                <button 
                    (click)="refreshAnswers()" 
                    class="ml-2 text-blue-500 hover:text-blue-700 transition-colors"
                    [disabled]="loading"
                    title="Refresh jawaban"
                >
                    <span class="pi pi-refresh text-xs" [class.pi-spin]="loading"></span>
                </button>
            </div>
            <div [ngClass]="inline ? 'mb-4' : 'mb-8'" *ngIf="data.diagnosa">
                <span class="font-semibold text-blue-700 mb-2 block">Diagnosa</span>
                <div class="rounded-xl p-0 text-base shadow-sm" *ngIf="parseDiagnosa(data.diagnosa) as diagnosa">
                    <div *ngIf="diagnosa.diagnosaUtama" [ngClass]="inline ? 'bg-blue-50 border border-blue-100 rounded-lg p-2 text-gray-800 shadow-sm mb-2 text-sm' : 'bg-blue-50 border border-blue-100 rounded-xl p-4 text-gray-800 shadow-sm mb-4'">
                        Diagnosa Utama: {{ diagnosa.diagnosaUtama || '-' }}
                    </div>
                    <div *ngIf="diagnosa.diagnosaBanding1" [ngClass]="inline ? 'bg-blue-50 border border-blue-100 rounded-lg p-2 text-gray-800 shadow-sm mb-2 text-sm' : 'bg-blue-50 border border-blue-100 rounded-xl p-4 text-gray-800 shadow-sm mb-4'">
                        Diagnosa Banding 1: {{ diagnosa.diagnosaBanding1 || '-' }}
                    </div>
                    <div *ngIf="diagnosa.diagnosaBanding2" [ngClass]="inline ? 'bg-blue-50 border border-blue-100 rounded-lg p-2 text-gray-800 shadow-sm text-sm' : 'bg-blue-50 border border-blue-100 rounded-xl p-4 text-gray-800 shadow-sm mt-2'">
                        Diagnosa Banding 2: {{ diagnosa.diagnosaBanding2 || '-' }}
                    </div>
                    <!-- Fallback jika tidak ada diagnosa yang ter-parse dengan baik -->
                    <div *ngIf="!diagnosa.diagnosaUtama && !diagnosa.diagnosaBanding1 && !diagnosa.diagnosaBanding2" [ngClass]="inline ? 'bg-blue-50 border border-blue-100 rounded-lg p-2 text-gray-800 shadow-sm text-sm' : 'bg-blue-50 border border-blue-100 rounded-xl p-4 text-gray-800 shadow-sm'">
                        {{ data.diagnosa }}
                    </div>
                </div>
            </div>
            <div [ngClass]="inline ? 'mb-4' : 'mb-8'" *ngIf="data.resep">
                <span class="font-semibold text-blue-700 mb-2 block">Resep</span>
                <div [ngClass]="inline ? 'bg-white border-2 border-dashed border-blue-400 rounded-lg p-3 shadow-md max-w-full relative overflow-hidden text-sm' : 'bg-white border-2 border-dashed border-blue-400 rounded-2xl p-6 shadow-md max-w-lg mx-auto relative overflow-hidden'" style="font-family: 'Georgia', serif;">
                    <div [ngClass]="inline ? 'absolute left-3 top-3 text-2xl text-blue-400 font-bold select-none' : 'absolute left-6 top-6 text-4xl text-blue-400 font-bold select-none'" style="font-family: 'Times New Roman', Times, serif;">℞</div>
                    <div [ngClass]="inline ? 'pl-8' : 'pl-16'">
                        <ng-container *ngFor="let r of parseResep(data.resep); let i = index">
                            <div *ngIf="r && r.trim() !== ''" class="flex items-start mb-2">
                                <span class="font-bold text-blue-700 mr-2">R{{i+1}}/</span>
                                <span class="tracking-wide text-gray-900" style="font-family: 'Georgia', serif;">{{r}}</span>
                            </div>
                        </ng-container>
                    </div>
                    <div [ngClass]="inline ? 'absolute bottom-1 right-2 text-xs text-blue-300 italic select-none' : 'absolute bottom-2 right-6 text-xs text-blue-300 italic select-none'">Resep elektronik</div>
                </div>
            </div>
            
            <!-- Pemeriksaan untuk tampilan inline -->
            <div *ngIf="inline && data.sheet_details && data.sheet_details.length" class="mb-4">
                <span class="font-semibold text-blue-700 mb-2 block">Pemeriksaan</span>
                
                <!-- Info tentang pemeriksaan yang dipilih untuk inline -->
                <div *ngIf="data.sheet_detail_id" class="mb-3 p-2 bg-green-50 border border-green-200 rounded-lg">
                    <div class="flex items-center gap-2 text-green-800">
                        <span class="pi pi-check-circle text-green-600 text-sm"></span>
                        <span class="font-medium text-xs">
                            {{ getSelectedSheetDetails(data.sheet_details || []).length }} dari {{ (data.sheet_details || []).length }} pemeriksaan dipilih
                        </span>
                    </div>
                </div>
                
                <ng-container *ngFor="let group of pemeriksaanGroups()">
                    <div class="mb-4">
                        <div class="font-bold text-blue-800 mb-2 text-sm flex items-center gap-1">
                            <span class="inline-block w-1.5 h-1.5 rounded-full bg-blue-400"></span>
                            {{ group.name }}
                        </div>
                        <ng-container *ngIf="group.header && group.rows.length">
                            <div class="overflow-x-auto rounded-lg border border-blue-200 shadow-sm">
                                <table class="min-w-full bg-white rounded-lg text-xs" style="border-collapse: separate; border-spacing: 0;">
                                    <thead>
                                        <tr>
                                            <th *ngFor="let h of group.header" class="px-3 py-2 border-b border-blue-200 bg-blue-50 text-blue-900 text-left font-semibold text-xs whitespace-nowrap">{{ h }}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr *ngFor="let row of group.rows" class="hover:bg-blue-50 transition">
                                            <td *ngFor="let cell of row" class="px-3 py-2 border-b border-blue-100 text-gray-800 whitespace-nowrap text-xs">
                                                <span [innerHTML]="cell && cell.trim() !== '' ? cell : '-'"></span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div *ngIf="group.interpretasi" class="mt-2">
                                <label class="block mb-1 font-semibold text-blue-900 text-xs">
                                    Interpretasi
                                </label>
                                <div class="bg-blue-50 border border-blue-100 rounded-lg p-2 text-gray-800 shadow-sm text-xs">{{ group.interpretasi }}</div>
                            </div>
                        </ng-container>
                        <!-- Fallback untuk tampilan non-tabel dalam inline -->
                        <ng-container *ngIf="!group.header || !group.rows.length">
                            <ng-container *ngFor="let detail of group.details">
                                <div class="bg-blue-50 border border-blue-100 rounded-lg p-2 shadow-sm text-gray-900 mb-2 text-xs" [innerHTML]="parseContent(detail.content)"></div>
                                <div *ngIf="getInterpretasi(detail)" class="mt-2">
                                    <label class="block mb-1 font-semibold text-blue-900 text-xs">
                                        Interpretasi
                                    </label>
                                    <div class="bg-blue-50 border border-blue-100 rounded-lg p-2 text-gray-800 shadow-sm text-xs">{{ getInterpretasi(detail) }}</div>
                                </div>
                            </ng-container>
                        </ng-container>
                    </div>
                </ng-container>
            </div>
            <div *ngIf="!inline" class="mb-4">
                <span class="font-semibold text-blue-700 mb-4 block">Pemeriksaan</span>
                
                <!-- Info tentang pemeriksaan yang dipilih -->
                <div *ngIf="data.sheet_detail_id" class="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div class="flex items-center gap-2 text-green-800">
                        <span class="pi pi-check-circle text-green-600"></span>
                        <span class="font-medium text-sm">
                            Menampilkan {{ getSelectedSheetDetails(data.sheet_details || []).length }} pemeriksaan yang dipilih peserta
                            dari {{ (data.sheet_details || []).length }} pemeriksaan yang tersedia
                        </span>
                    </div>
                </div>
                
                <div *ngIf="data.sheet_details && data.sheet_details.length">
                    <ng-container *ngFor="let group of pemeriksaanGroups()">
                        <div class="mb-8">
                            <div class="font-bold text-blue-800 mb-3 uppercase tracking-wide text-lg flex items-center gap-2">
                                <span class="inline-block w-2 h-2 rounded-full bg-blue-400"></span>
                                {{ group.name }}
                            </div>
                            <ng-container *ngIf="group.header && group.rows.length">
                                <div class="overflow-x-auto rounded-xl border border-blue-200 shadow-sm">
                                    <table class="min-w-full bg-white rounded-xl" style="border-collapse: separate; border-spacing: 0;">
                                        <thead class="sticky top-0 z-10">
                                            <tr>
                                                <th *ngFor="let h of group.header" class="px-6 py-3 border-b border-blue-200 bg-blue-50 text-blue-900 text-left font-semibold text-base whitespace-nowrap sticky top-0 z-10">{{ h }}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr *ngFor="let row of group.rows" class="hover:bg-blue-50 transition">
                                                <td *ngFor="let cell of row" class="px-6 py-3 border-b border-blue-100 text-gray-800 whitespace-nowrap">
                                                    <span [innerHTML]="cell && cell.trim() !== '' ? cell : '-'"></span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div *ngIf="group.interpretasi" class="mt-4">
                                    <label class="block mb-2 font-semibold text-blue-900">
                                        Interpretasi
                                    </label>
                                    <div class="bg-blue-50 border border-blue-100 rounded-xl p-4 text-gray-800 shadow-sm">{{ group.interpretasi }}</div>
                                </div>
                            </ng-container>
                            <!-- Jika tidak ada header, fallback ke tampilan lama -->
                            <ng-container *ngIf="!group.header || !group.rows.length">
                                <ng-container *ngFor="let detail of group.details">
                                    <ng-container *ngIf="isArrayContent(detail)">
                                        <div class="overflow-x-auto rounded-xl border border-blue-200 shadow-sm mb-4">
                                            <table class="min-w-full bg-white rounded-xl" style="border-collapse: separate; border-spacing: 0;">
                                                <tbody>
                                                    <tr class="hover:bg-blue-50 transition">
                                                        <td *ngFor="let cell of getArrayContent(detail)" class="px-6 py-3 border-b border-blue-100 text-gray-800 whitespace-nowrap">
                                                            <span [innerHTML]="cell && cell.trim() !== '' ? cell : '-'"></span>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </ng-container>
                                    <ng-container *ngIf="isImage(detail)">
                                        <div class="mb-4 flex justify-center w-full">
                                            <div class="rounded-xl w-full overflow-hidden border border-blue-100 shadow-sm bg-blue-50 p-2" [innerHTML]="detail.content"></div>
                                        </div>
                                    </ng-container>
                                    <ng-container *ngIf="!isArrayContent(detail) && !isImage(detail)">
                                        <div class="prose prose-sm max-w-none bg-blue-50 border border-blue-100 rounded-xl p-4 shadow-sm text-gray-900 mb-2" [innerHTML]="parseContent(detail.content)"></div>
                                    </ng-container>
                                    <div *ngIf="getInterpretasi(detail)" class="mt-4">
                                        <label class="block mb-2 font-semibold text-blue-900">
                                            Interpretasi
                                            <ng-container *ngIf="getInterpretasiTitle(detail) as title">
                                                <span *ngIf="title" class="font-normal text-gray-700">|| {{ title }}</span>
                                            </ng-container>
                                        </label>
                                        <div class="bg-blue-50 border border-blue-100 rounded-xl p-4 text-gray-800 shadow-sm">{{ getInterpretasi(detail) }}</div>
                                    </div>
                                </ng-container>
                            </ng-container>
                        </div>
                    </ng-container>
                </div>
            </div>
        </ng-container>
        </ng-container>
        <ng-template #noAnswers>
            <div [ngClass]="inline ? 'text-center py-6' : 'text-center py-12'">
                <div [ngClass]="inline ? 'text-2xl mb-2' : 'text-4xl mb-4'" class="text-gray-400">
                    <span class="pi pi-inbox"></span>
                </div>
                <div [ngClass]="inline ? 'text-base font-bold text-gray-700 mb-2' : 'text-xl font-bold text-gray-700 mb-3'">
                    Belum Ada Jawaban
                </div>
                <div [ngClass]="inline ? 'text-sm text-gray-600 mb-3' : 'text-base text-gray-600 mb-4'">
                    Peserta <strong>{{ user?.name || user?.ID_USER || '-' }}</strong> belum mengerjakan
                    <br><strong>{{ getCurrentOsceCaseName() }}</strong>
                </div>
                <div [ngClass]="inline ? 'text-xs text-gray-500 mb-3' : 'text-sm text-gray-500 mb-4'">
                    Sistem akan memperbarui otomatis setiap 5 menit
                </div>
                <button 
                    (click)="refreshAnswers()" 
                    [ngClass]="inline ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'"
                    class="bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2 mx-auto"
                    [disabled]="loading"
                >
                    <span class="pi pi-refresh" [class.pi-spin]="loading"></span>
                    {{ loading ? 'Memuat...' : 'Periksa Ulang' }}
                </button>
                <div *ngIf="lastRefreshTime" [ngClass]="inline ? 'text-xs text-gray-400 mt-2' : 'text-sm text-gray-400 mt-3'">
                    Terakhir diperbarui: {{ getLastRefreshTimeFormatted() }}
                </div>
            </div>
        </ng-template>
        <ng-template #loadingTpl>
            <div [ngClass]="inline ? 'text-center py-8' : 'text-center py-12'">
                <div [ngClass]="inline ? 'text-2xl mb-2' : 'text-3xl mb-3'" class="text-blue-500">
                    <span class="pi pi-spin pi-spinner"></span>
                </div>
                <div [ngClass]="inline ? 'text-sm text-blue-700' : 'text-lg text-blue-700'">
                    Memuat detail jawaban...
                </div>
            </div>
        </ng-template>
    </div>
</div>
    `
})
export class SheetUserAnswerDetail implements OnInit, OnDestroy, OnChanges {
    @Input() simulationId: any;
    @Input() osceId: any;
    @Input() sheetId: any;
    @Input() user: any; // Tambahan input untuk user data
    @Input() inline: boolean = false; // Properti untuk tampilan inline
    @Input() currentOsceCase: any = null; // Add current OSCE case data for better display
    @Output() close = new EventEmitter<void>();
    data: any = null;
    loading: boolean = false;
    private refreshInterval: any = null;
    lastRefreshTime: Date | null = null;

    // Tambahan properti untuk menghilangkan error
    id: any;
    lastIds: Set<number> = new Set();

    constructor(
        private route: ActivatedRoute,
        private userService: UserService
    ) {}

    ngOnInit() {
        // Set id dari osceId (atau sesuaikan jika perlu)
        this.id = this.osceId;
        
        // Only fetch answers if we have the required data
        if (this.user?.ID_USER && this.osceId && this.simulationId) {
            this.fetchAnswers();
            // Set up automatic refresh every 5 minutes (300000 ms)
            this.startAutoRefresh();
        } else {
            console.warn('❌ Missing required data for fetching answers:', {
                userId: this.user?.ID_USER,
                osceId: this.osceId,
                simulationId: this.simulationId,
                hasUser: !!this.user
            });
            this.data = null;
            this.loading = false;
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['osceId'] || changes['simulationId'] || changes['user'] || changes['sheetId']) {
            this.id = this.osceId;
            if (this.user?.ID_USER && this.osceId && this.simulationId) {
                this.fetchAnswers();
                this.startAutoRefresh();
            } else {
                this.data = null;
                this.loading = false;
            }
        }
    }

    ngOnDestroy() {
        // Clean up the refresh interval
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = null;
        }
    }

    private startAutoRefresh() {
        // Clear any existing interval
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
        }
        
        // Set up new interval for 5 minutes (300000 ms)
        this.refreshInterval = setInterval(() => {
            this.fetchAnswers();
        }, 300000); // 5 minutes
        
    }

    fetchAnswers() {
        // Check if user and osceId are available
        if (!this.user?.ID_USER || !this.osceId || !this.simulationId) {
            console.warn('❌ Missing required parameters for fetching answers:', {
                userId: this.user?.ID_USER,
                osceId: this.osceId,
                simulationId: this.simulationId
            });
            this.data = null;
            this.loading = false;
            return;
        }

        this.loading = true;

        // Use the correct parameter structure for the API call
        const requestData: any = {
            simulation_id: this.simulationId, 
            osce_id: this.osceId,
            user_id: this.user.ID_USER
        };

        if (this.sheetId) {
            requestData.sheet_id = this.sheetId;
        }
        
        this.userService.getSheetAnswer(requestData).then((res: any) => {
            
            if (res.status && res.data) {
                // Handle both single object and array responses
                let answers = Array.isArray(res.data) ? res.data : [res.data];
                                
                // Filter by current OSCE ID and user ID with more flexible matching
                const filteredAnswers = answers.filter((answer: any) => {
                    const matchesOsce = String(answer.osce_id) === String(this.osceId) || 
                                      String(answer.ID_OSCE) === String(this.osceId) ||
                                      String(answer.osceid) === String(this.osceId);
                    
                    const matchesUser = String(answer.user_id) === String(this.user.ID_USER) || 
                                      String(answer.ID_USER) === String(this.user.ID_USER) ||
                                      String(answer.userid) === String(this.user.ID_USER);
                    
                    return matchesOsce && matchesUser;
                });
                
                if (filteredAnswers.length > 0) {
                    // Take the most recent answer if multiple matches
                    const sortedAnswers = filteredAnswers.sort((a: any, b: any) => {
                        const dateA = new Date(a.created_at || a.tanggal || 0);
                        const dateB = new Date(b.created_at || b.tanggal || 0);
                        return dateB.getTime() - dateA.getTime();
                    });
                    
                    this.data = sortedAnswers[0];
                } else {
                    this.data = null;
                }
            } else {
                this.data = null;
            }
        }).catch((error) => {
            console.error(`❌ Error fetching answers for ${this.getCurrentOsceCaseName()}:`, error);
            this.data = null;
        }).finally(() => {
            this.loading = false;
            this.lastRefreshTime = new Date();
        });
    }

    // Cek apakah detail berupa tabel (hanya jika ada header dan array bukan single content array)
    isTable(detail: any): boolean {
        try {
            if (!detail.header) return false;
            const arr = JSON.parse(detail.content);
            // Bukan single content array
            if (this.isSingleContentArray(detail)) return false;
            return Array.isArray(arr) && !!detail.header;
        } catch {
            return false;
        }
    }

    // Cek jika content berupa array string, hanya elemen pertama yang ada, sisanya kosong
    isSingleContentArray(detail: any): boolean {
        try {
            const arr = JSON.parse(detail.content);
            if (Array.isArray(arr) && arr.length > 0) {
                // Jika semua setelah index 0 kosong string
                return arr.slice(1).every((v: any) => v === "") && typeof arr[0] === "string" && arr[0].trim() !== "";
            }
        } catch {}
        return false;
    }

    getFirstContent(content: string): string {
        try {
            const arr = JSON.parse(content);
            if (Array.isArray(arr) && arr.length > 0) {
                return arr[0];
            }
        } catch {}
        return content;
    }

    // Cek apakah detail berupa gambar
    isImage(detail: any): boolean {
        return typeof detail.content === 'string' && detail.content.includes('<img');
    }

    // Tampilkan sebagai tabel jika content array
    isArrayContent(detail: any): boolean {
        try {
            const arr = JSON.parse(detail.content);
            return Array.isArray(arr);
        } catch {
            return false;
        }
    }

    getArrayContent(detail: any): string[] {
        try {
            const arr = JSON.parse(detail.content);
            return Array.isArray(arr) ? arr : [];
        } catch {
            return [];
        }
    }

    // Ambil interpretasi jika ada (misal dari data.pemeriksaan)
    getInterpretasi(detail: any): string | null {
        if (!this.data?.pemeriksaan) return null;
        try {
            const pemeriksaan = JSON.parse(this.data.pemeriksaan);
            const key = detail.sheet_name + '||' + (detail.header || 'null');
            return pemeriksaan[key] || null;
        } catch {
            return null;
        }
    }

    getInterpretasiTitle(detail: any): string | null {
        if (!this.data?.pemeriksaan) return null;
        try {
            const pemeriksaan = JSON.parse(this.data.pemeriksaan);
            // Cari key yang cocok
            for (const key of Object.keys(pemeriksaan)) {
                const [title, header] = key.split('||');
                if (
                    title === detail.sheet_name &&
                    (
                        (!detail.header && (header === 'null' || header === undefined)) ||
                        (detail.header && header === detail.header)
                    )
                ) {
                    // Jika header berupa array, tampilkan sebagai tabel
                    if (header && header.startsWith('[')) {
                        try {
                            const arr = JSON.parse(header);
                            if (Array.isArray(arr)) {
                                return arr.join(' | ');
                            }
                        } catch {}
                    }
                    return header && header !== 'null' ? header : '';
                }
            }
        } catch {}
        return null;
    }

    // Ambil header tabel
    parseHeader(header: string): string[] {
        if (!header) return [];
        try {
            const parsed = JSON.parse(header);
            return Array.isArray(parsed) ? parsed : [parsed];
        } catch {
            return [header];
        }
    }

    // Ambil baris tabel
    getTableRows(detail: any): string[][] {
        const headers = this.parseHeader(detail.header);
        let allRows: string[][] = [];
        try {
            const arr = JSON.parse(detail.content);
            if (Array.isArray(arr[0])) {
                // Jika array of array
                allRows = arr;
            } else {
                // Single row
                allRows = [arr];
            }
        } catch {
            // skip
        }
        // Pastikan setiap row panjangnya sama dengan headers
        return allRows.map(row => {
            const r = Array.isArray(row) ? row : [];
            return headers.map((_, i) => r[i] !== undefined ? r[i] : '-');
        });
    }

    parseContent(content: string): string {
        try {
            const arr = JSON.parse(content);
            if (Array.isArray(arr)) {
                return arr.filter(Boolean).map((c: string) => c).join('<br>');
            }
        } catch {
            // Bukan array, return apa adanya
        }
        return content;
    }

    parseDiagnosa(json: string): any {
        if (!json) return {};
        try { 
            const parsed = JSON.parse(json);
            // Handle different possible structures
            if (typeof parsed === 'object') {
                return {
                    diagnosaUtama: parsed.diagnosaUtama || parsed.diagnosa_utama || parsed.primary_diagnosis || '',
                    diagnosaBanding1: parsed.diagnosaBanding1 || parsed.diagnosa_banding_1 || parsed.differential_1 || '',
                    diagnosaBanding2: parsed.diagnosaBanding2 || parsed.diagnosa_banding_2 || parsed.differential_2 || ''
                };
            }
            return {};
        } catch { 
            // If not JSON, treat as plain text diagnosa
            return {
                diagnosaUtama: json,
                diagnosaBanding1: '',
                diagnosaBanding2: ''
            };
        }
    }
    parsePemeriksaan(json: string): any {
        try { return JSON.parse(json); } catch { return {}; }
    }
    pemeriksaanKeys(obj: any): string[] {
        return obj ? Object.keys(obj) : [];
    }
    parseResep(json: string): any[] {
        if (!json) return [];
        try { 
            const parsed = JSON.parse(json);
            if (Array.isArray(parsed)) {
                return parsed.filter(item => item && item.trim() !== '');
            } else if (typeof parsed === 'string') {
                return [parsed];
            } else if (typeof parsed === 'object') {
                // Convert object to array of values
                return Object.values(parsed).filter((item: any) => item && typeof item === 'string' && item.trim() !== '');
            }
            return [];
        } catch { 
            // If not JSON, treat as plain text and split by lines
            return json.split('\n').filter(line => line && line.trim() !== '');
        }
    }

    // Group sheet_details by id (not by name)
    groupedSheetDetails() {
        if (!this.data?.sheet_details) return [];
        const groups: { [id: string]: any[] } = {};
        for (const detail of this.data.sheet_details) {
            if (!groups[detail.id]) groups[detail.id] = [];
            groups[detail.id].push(detail);
        }
        return Object.keys(groups).map(id => ({
            id,
            details: groups[id]
        }));
    }

    // Deteksi jika content adalah array string dan semua elemen setelah pertama tidak kosong
    isHorizontalTable(detail: any): boolean {
        try {
            const arr = JSON.parse(detail.content);
            // Cek: array, length > 1, elemen pertama string, dan setidaknya satu elemen setelah pertama tidak kosong
            if (
                Array.isArray(arr) &&
                arr.length > 1 &&
                typeof arr[0] === "string" &&
                arr.slice(1).some((v: any) => v && v.trim() !== "")
            ) {
                return true;
            }
        } catch {}
        return false;
    }

    getHorizontalHeaders(detail: any): string[] {
        try {
            const arr = JSON.parse(detail.content);
            // Ambil semua header setelah index 0
            return arr.slice(1);
        } catch {
            return [];
        }
    }

    getHorizontalValues(detail: any): string[] {
        try {
            const arr = JSON.parse(detail.content);
            // Ambil semua value setelah index 0 (bisa diubah sesuai kebutuhan)
            return arr.slice(1);
        } catch {
            return [];
        }
    }

    // Grouping pemeriksaan berdasarkan key LABORATORIUM, RADIOLOGI, dll
    pemeriksaanGroups() {
        const pemeriksaan = this.parsePemeriksaan(this.data?.pemeriksaan);
        const allDetails = this.data?.sheet_details || [];
        
        // Filter only selected sheet details based on sheet_detail_id
        const selectedDetails = this.getSelectedSheetDetails(allDetails);
        
        const groups: any[] = [];

        for (const key of Object.keys(pemeriksaan)) {
            // Pisahkan nama dan header
            let [name, headerStr] = key.split('||');
            let header: string[] = [];
            try {
                if (headerStr && headerStr.startsWith('[')) {
                    header = JSON.parse(headerStr);
                }
            } catch {}
            
            // Ambil HANYA detail yang terpilih berdasarkan sheet_detail_id dan sheet_name === nama pemeriksaan
            let groupDetails: any[] = [];
            if (header.length) {
                groupDetails = selectedDetails.filter((d: any) => d.sheet_name === name);
            } else {
                groupDetails = selectedDetails.filter((d: any) => d.sheet_name === name);
            }
            
            // Gabungkan semua detail yang cocok ke satu tabel jika array dan jumlah kolom sama dengan header
            let rows: string[][] = [];
            if (header.length && groupDetails.length) {
                for (const detail of groupDetails) {
                    try {
                        const arr = JSON.parse(detail.content);
                        if (Array.isArray(arr) && arr.length === header.length) {
                            rows.push(arr);
                        }
                    } catch {}
                }
            }
            
            // Only add group if there are selected details for this group
            if (groupDetails.length > 0) {
                groups.push({
                    name,
                    header: header.length ? header : null,
                    rows,
                    interpretasi: pemeriksaan[key],
                    details: groupDetails
                });
            }
        }
        
        // Tambahkan detail lain yang tidak ada di pemeriksaan tapi masih terpilih
        const usedDetailIds = new Set(groups.flatMap(g => g.details.map((d: any) => d.id)));
        const otherSelectedDetails = selectedDetails.filter((d: any) => !usedDetailIds.has(d.id));
        for (const detail of otherSelectedDetails) {
            groups.push({
                name: detail.name,
                header: null,
                rows: [],
                interpretasi: null,
                details: [detail]
            });
        }
        
        return groups;
    }

    // Method to get only selected sheet details based on sheet_detail_id
    getSelectedSheetDetails(allDetails: any[]): any[] {
        if (!this.data?.sheet_detail_id || !allDetails.length) {
            return allDetails; // Return all if no filter specified
        }

        let selectedIds: string[] = [];
        try {
            if (typeof this.data.sheet_detail_id === 'string') {
                let parsed;
                try {
                    parsed = JSON.parse(this.data.sheet_detail_id);
                } catch {
                    parsed = this.data.sheet_detail_id;
                }
                if (Array.isArray(parsed)) {
                    selectedIds = parsed.map((id: any) => String(id));
                } else {
                    selectedIds = [String(parsed)];
                }
            } else if (Array.isArray(this.data.sheet_detail_id)) {
                selectedIds = this.data.sheet_detail_id.map((id: any) => String(id));
            } else if (this.data.sheet_detail_id !== undefined && this.data.sheet_detail_id !== null) {
                selectedIds = [String(this.data.sheet_detail_id)];
            }
        } catch (error) {
            console.error('Error parsing sheet_detail_id:', error);
            return allDetails; // Return all if parsing fails
        }

        // Filter details based on selected IDs
        const filteredDetails = allDetails.filter((detail: any) => 
            selectedIds.includes(String(detail.id))
        );

        return filteredDetails;
    }

    // Get current OSCE case name for display
    getCurrentOsceCaseName(): string {
        if (this.currentOsceCase) {
            return this.currentOsceCase.TITLE || this.currentOsceCase.NAMA_OSCE || this.currentOsceCase.name || 'OSCE ini';
        }
        return 'penyakit/kasus OSCE ini';
    }

    // Get formatted last refresh time
    getLastRefreshTimeFormatted(): string {
        if (!this.lastRefreshTime) return 'Belum pernah dimuat';
        
        const now = new Date();
        const diffMs = now.getTime() - this.lastRefreshTime.getTime();
        const diffMinutes = Math.floor(diffMs / 60000);
        
        if (diffMinutes < 1) return 'Baru saja';
        if (diffMinutes === 1) return '1 menit yang lalu';
        if (diffMinutes < 60) return `${diffMinutes} menit yang lalu`;
        
        const diffHours = Math.floor(diffMinutes / 60);
        if (diffHours === 1) return '1 jam yang lalu';
        if (diffHours < 24) return `${diffHours} jam yang lalu`;
        
        return this.lastRefreshTime.toLocaleString('id-ID');
    }

    // Manual refresh method
    refreshAnswers(): void {
        this.fetchAnswers();
    }

    // Helper method to check if data has meaningful content
    hasAnswerContent(): boolean {
        if (!this.data) return false;
        return !!(this.data.diagnosa || this.data.resep || (this.data.sheet_details && this.data.sheet_details.length > 0));
    }

    // Helper method to get answer summary
    getAnswerSummary(): string {
        if (!this.data) return 'Belum ada jawaban';
        
        const parts = [];
        if (this.data.diagnosa) parts.push('Diagnosa');
        if (this.data.resep) parts.push('Resep');
        if (this.data.sheet_details && this.data.sheet_details.length > 0) {
            parts.push(`${this.data.sheet_details.length} Pemeriksaan`);
        }
        
        return parts.length > 0 ? parts.join(', ') : 'Data tersedia';
    }

}